//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useTranslation } from 'react-i18next';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';

export interface User {
  id: string;
  username: string;
  email: string;
  userid: string;
  firstname: string | null;
  lastname: string | null;
  enabled: boolean;
  roles: string[];
  teamspaces?: string[];
  loops?: string[];
  name: string;
  initials: string;
  action: string;
  tier?: string;
}

export interface ApiUser {
  id: string;
  username: string;
  email: string;
  userid: string;
  firstname: string | null;
  lastname: string | null;
  inDefaultTeamSpace: string | null;
  roles: string[];
  authorities: { authority: string }[];
  enabled: boolean;
  credentialsNonExpired: boolean;
  accountNonExpired: boolean;
  accountNonLocked: boolean;
  teamspaces?: string[];
  loops?: string[];
  tier?: string;
  _links: {
    [key: string]: { href: string };
  };
}

export interface UserProfileListRef {
  updateUserState: (updatedUser: User) => void;
  showSuccessAlert: (message: string) => void;
}

interface UserProfileListProps {
  users: ApiUser[];
  onUserSelect: (user: User) => void;
  searchQuery?: string;
  assignLicense: (userId: string, tier: string) => Promise<any>;
  defaultSelectedUserId?: string;
  isCurrentUserFn: (user: User) => boolean;
  onConfirmRevoke?: (user: User, newTier: string) => void;
}

const TIER_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'nexus-t1', label: 'Essentials' },
  { value: 'nexus-t2', label: 'Standard' },
  { value: 'nexus-t3', label: 'Premium' },
];

const UserProfileList = forwardRef<UserProfileListRef, UserProfileListProps>(
  function UserProfileList(
    {
      users,
      onUserSelect,
      searchQuery = '',
      assignLicense,
      defaultSelectedUserId = '',
      isCurrentUserFn,
      onConfirmRevoke,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

    // Keep a stable ref for onUserSelect to avoid it being an effect dependency
    const onUserSelectRef = useRef(onUserSelect);
    useEffect(() => {
      onUserSelectRef.current = onUserSelect;
    });

    // Exclude technical service accounts from the list view
    // Pattern-based filtering to catch all service accounts, not just specific ones
    const isServiceAccount = useCallback((username: string): boolean => {
      const lowerUsername = username?.toLowerCase() || '';
      return (
        lowerUsername.startsWith('service-account-') ||
        lowerUsername.startsWith('devopslicensing')
      );
    }, []);

    // Local overrides for optimistic UI updates (e.g. tier changes).
    // We store the users snapshot alongside overrides so useMemo can
    // detect stale overrides without accessing a ref during render or
    // calling setState inside an effect.
    const [overrideState, setOverrideState] = useState<{
      snapshot: typeof users;
      overrides: Record<string, Partial<User>>;
    }>({ snapshot: users, overrides: {} });

    // Stable accessor used by updateUserState and handleTierChange
    const userOverrides =
      overrideState.snapshot === users ? overrideState.overrides : {};

    const setUserOverrides = useCallback(
      (
        updater: (
          prev: Record<string, Partial<User>>,
        ) => Record<string, Partial<User>>,
      ) => {
        setOverrideState((prev) => ({
          snapshot: prev.snapshot,
          overrides: updater(prev.overrides),
        }));
      },
      [],
    );

    // Derive processedUsers from props + overrides (no useState/useEffect chain)
    const processedUsers = useMemo(() => {
      if (!Array.isArray(users)) return [];

      // When users identity changed, ignore stale overrides
      const activeOverrides =
        overrideState.snapshot === users ? overrideState.overrides : {};

      return users
        .filter((user) => !isServiceAccount(user.username))
        .map((user) => {
          const name =
            [user.firstname, user.lastname].filter(Boolean).join(' ') ||
            user.username;

          const initials =
            [user.firstname, user.lastname]
              .filter(Boolean)
              .map((n) => n?.[0]?.toUpperCase())
              .join('') ||
            user.username?.[0]?.toUpperCase() ||
            '?';

          const action = user.enabled ? 'Revoke' : 'Assign';

          const base: User = {
            ...user,
            name,
            initials,
            action,
            tier: user.tier || 'none',
          };

          const override = activeOverrides[user.id];
          return override ? { ...base, ...override } : base;
        })
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
    }, [users, overrideState, isServiceAccount]);

    // Derive displayedUsers from processedUsers + searchQuery (no useState/useEffect chain)
    const displayedUsers = useMemo(() => {
      if (!searchQuery.trim()) return processedUsers;

      const lowercaseQuery = searchQuery.toLowerCase().trim();
      return processedUsers.filter(
        (user) =>
          user.name.toLowerCase().includes(lowercaseQuery) ||
          user.email.toLowerCase().includes(lowercaseQuery) ||
          user.username.toLowerCase().includes(lowercaseQuery),
      );
    }, [searchQuery, processedUsers]);

    // Track user-clicked selection separately from the derived/auto selection
    const [manualSelectedUserId, setManualSelectedUserId] =
      useState<string>('');

    // Compute the effective selectedUserId without a self-triggering useEffect
    const selectedUserId = useMemo(() => {
      // 1. Manual click takes highest priority while the user is still in the list
      if (
        manualSelectedUserId &&
        displayedUsers.some((u) => u.id === manualSelectedUserId)
      ) {
        return manualSelectedUserId;
      }
      // 2. Fall back to parent-requested default if it exists in the list
      if (
        defaultSelectedUserId &&
        displayedUsers.some((u) => u.id === defaultSelectedUserId)
      ) {
        return defaultSelectedUserId;
      }
      // 3. Fall back to first displayed user
      return displayedUsers.length > 0 ? displayedUsers[0].id : '';
    }, [defaultSelectedUserId, manualSelectedUserId, displayedUsers]);

    // Track selectedUserId in a ref so updateUserState can check it without being a dependency
    const selectedUserIdRef = useRef(selectedUserId);
    useEffect(() => {
      selectedUserIdRef.current = selectedUserId;
    });

    // Notify parent when the effective selection changes — only via ref to avoid dependency loops
    const prevSelectedIdRef = useRef('');
    useEffect(() => {
      if (selectedUserId !== prevSelectedIdRef.current) {
        prevSelectedIdRef.current = selectedUserId;
        const selectedUser = displayedUsers.find(
          (u) => u.id === selectedUserId,
        );
        if (selectedUser) {
          onUserSelectRef.current(selectedUser);
        }
      }
    }, [selectedUserId, displayedUsers]);

    const updateUserState = useCallback(
      (updatedUser: User) => {
        setUserOverrides((prev) => ({
          ...prev,
          [updatedUser.id]: updatedUser,
        }));

        // Only notify parent if the updated user is the currently selected one
        if (updatedUser.id === selectedUserIdRef.current) {
          onUserSelectRef.current(updatedUser);
        }
      },
      [setUserOverrides],
    );

    const handleTierChange = useCallback(
      async (user: User, newTier: string) => {
        // If selecting "none" (revoke), show confirmation dialog
        if (newTier === 'none' && user.tier !== 'none' && onConfirmRevoke) {
          onConfirmRevoke(user, newTier);
          return;
        }

        const shouldEnable = newTier !== 'none';
        const optimisticUser = {
          ...user,
          tier: newTier,
          enabled: shouldEnable,
          action: shouldEnable ? 'Revoke' : 'Assign',
        };

        updateUserState(optimisticUser);

        try {
          await assignLicense(user.id, newTier);
          const tierLabel =
            TIER_OPTIONS.find((t) => t.value === newTier)?.label || newTier;
          const isRevoked = newTier === 'none';
          showSnackbar(
            isRevoked
              ? t('license-revoked-success', { name: user.name })
              : t('license-assigned-success', {
                  name: user.name,
                  tier: tierLabel,
                }),
            'success',
          );
        } catch (error) {
          console.error('Error updating license:', error);
          const revertedUser = {
            ...user,
            tier: user.tier || 'none',
            enabled: user.enabled,
            action: user.action,
          };
          updateUserState(revertedUser);
          showSnackbar(t('failed-to-update-license'), 'error');
        }
      },
      [assignLicense, onConfirmRevoke, updateUserState, showSnackbar, t],
    );

    const handleUserClick = (user: User) => {
      setManualSelectedUserId(user.id);
    };

    useImperativeHandle(ref, () => ({
      updateUserState,
      showSuccessAlert: (message: string) => showSnackbar(message, 'success'),
    }));

    return (
      <div
        className="max-h-full w-full overflow-auto"
        role="list"
        aria-label={t('user-profiles')}
        id="id-profile-list-container"
        data-testid="profile-list-container"
      >
        {displayedUsers.length === 0 ? (
          <div
            className="p-6 text-center text-sm text-gray-500"
            id="id-profile-list-empty-state"
            data-testid="profile-list-empty-state"
          >
            {searchQuery.trim()
              ? `${t('no-users-found')} "${searchQuery}"`
              : t('no-users-available')}
          </div>
        ) : (
          <div id="id-profile-list-users" data-testid="profile-list-users">
            {displayedUsers.map((user) => (
              <button
                key={user.id}
                type="button"
                className={`flex w-full select-none items-center gap-3 rounded px-4 py-3 text-left transition-colors ${
                  user.id === selectedUserId
                    ? 'bg-carbon-surfaceSecondary dark:bg-carbon-darkPanel'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => handleUserClick(user)}
                aria-current={user.id === selectedUserId ? 'true' : undefined}
              >
                <div
                  className="flex size-10 items-center justify-center rounded-full bg-brand-link text-base font-semibold text-white"
                  id="id-profile-list-avatar"
                  data-testid="profile-list-avatar"
                >
                  {user.initials || '?'}
                </div>
                <div
                  className="min-w-0 flex-1"
                  id="id-profile-list-user-info"
                  data-testid="profile-list-user-info"
                >
                  <div
                    className="truncate text-sm font-medium text-carbon-textPrimary dark:text-white"
                    id="id-profile-list-user-name"
                    data-testid="profile-list-user-name"
                  >
                    {user.name || 'Unknown'}
                    {isCurrentUserFn(user) && (
                      <span
                        className="ml-2 text-xs text-blue-600"
                        id="id-profile-list-you-badge"
                        data-testid="profile-list-you-badge"
                      >
                        ({t('you')})
                      </span>
                    )}
                  </div>
                  <div
                    className="truncate text-xs text-carbon-textSecondary dark:text-gray-400"
                    id="id-profile-list-user-email"
                    data-testid="profile-list-user-email"
                  >
                    {user.email}
                  </div>
                </div>
                <div
                  className="flex min-w-[90px] shrink-0 flex-col items-end gap-1"
                  id="id-profile-list-tier-section"
                  data-testid="profile-list-tier-section"
                >
                  <span
                    className="text-xs text-carbon-textSecondary dark:text-gray-400"
                    id="id-profile-list-tier-label"
                    data-testid="profile-list-tier-label"
                  >
                    {t('tier')}
                  </span>

                  <div
                    role="presentation"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    id="id-profile-list-tier-select-wrapper"
                    data-testid="profile-list-tier-select-wrapper"
                  >
                    <Select
                      value={user.tier || 'none'}
                      onValueChange={(value) => handleTierChange(user, value)}
                    >
                      <SelectTrigger
                        className="h-7 w-[100px] border-gray-300 text-xs"
                        aria-label={`${t('tier-level')} ${user.name}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />
      </div>
    );
  },
);

export default UserProfileList;
